import { NotificationType, INotification } from '@common/interfaces/notification'
import { ROUTES } from '../constants/routes'

export interface IMessageNotificationTarget {
  channelId: string
  messageId?: string
}

const MESSAGE_NOTIFICATION_TYPES = [
  NotificationType.ChannelMention,
  NotificationType.ChannelMessage,
]

/**
 * Mirrors the ChannelMention/ChannelMessage branch of getNotificationLocation
 * (common/helpers/notificationRouting.ts) — that helper builds a URL for the full
 * Messages page (also used for email/push links), while this extracts the same
 * target so an in-app click can load it into the floating bubble instead.
 */
export const getMessageNotificationTarget = (
  notification: INotification,
): IMessageNotificationTarget | null => {
  if (!MESSAGE_NOTIFICATION_TYPES.includes(notification.type)) {
    return null
  }
  const data = notification.data as { channelId?: string; messageId?: string } | null | undefined
  if (!data?.channelId) {
    return null
  }
  return {
    channelId: String(data.channelId),
    messageId: data.messageId ? String(data.messageId) : undefined,
  }
}

/** The AIM page and everything under it: where the page, not the bubble, hosts Messages. */
export const isMessagesRoute = (pathname: string): boolean =>
  pathname === ROUTES.MESSAGES || pathname.startsWith(`${ROUTES.MESSAGES}/`)

/**
 * The floating bubble can't host Messages while the user is already on the
 * Messages page (see the single-host invariant documented in Messages.tsx), or
 * when the bubble isn't mounted at all. In both cases fall back to the
 * notification's normal link rather than addressing a bubble that isn't there.
 *
 * The bubble is not mounted for two reasons, and both have to be asked about: it
 * is internal-user-only (the same `isInternalUser` gate MainLayout applies), and
 * below `md` it is not rendered at all — on a phone there is no bubble, only the
 * full Messages screen (MainLayout's `!isMobile` gate). Missing the second one
 * broke every message notification on mobile: the callers `preventDefault()` the
 * link and then dispatch to a bubble that isn't there, so a tap cancelled the
 * navigation that would have worked and did nothing in its place. `isMobile` is
 * therefore a required argument, not an optional one — a new call site must decide
 * rather than silently inherit the broken default.
 *
 * The dedicated Arc pages used to be a third exclusion, because MainLayout hid the
 * floating surface under /atlas-ai while that section rendered the Arc chat inline.
 * Arc lives inside a conversation now, nothing is rendered inline there, and
 * MessagesBubble mounts on every desktop route but /messages — so excluding those
 * pages would send a notification click navigating away from a bubble that was
 * available all along, which is the behaviour this whole path exists to avoid.
 */
export const shouldOpenMessageInBubble = (
  pathname: string,
  isInternalUser: boolean,
  isMobile: boolean,
): boolean => {
  if (!isInternalUser || isMobile) {
    return false
  }
  return !isMessagesRoute(pathname)
}

/**
 * Notification rows render as a real anchor (RouterLink or `<a>`), so a
 * ctrl/cmd/shift/middle-click carries the browser's own "open in a new
 * tab/window" convention — intercepting the click to open the bubble instead
 * would silently break that for every notification link. Only a plain,
 * unmodified left-click should be redirected into the bubble.
 */
export const isPlainLeftClick = (event: {
  button: number
  ctrlKey: boolean
  metaKey: boolean
  shiftKey: boolean
  altKey: boolean
}): boolean =>
  event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey
